import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";
import { useAuthStore, rolesAtLeast } from "@/stores/auth";
import CampaignsSection from "@/features/ads/CampaignsSection";
import UserPicker, { type PickedUser } from "@/features/iam/UserPicker";
import MockSeedDialog from "@/features/mock/MockSeedDialog";

interface AdAccount {
  id: string;
  organizationId: string;
  metaAccountId: string;
  name: string;
  currency: string | null;
  timezoneName: string | null;
  accountStatus: number | null;
  sandbox: boolean;
  createdAt: string;
}

interface Grant {
  id: string;
  userId: string;
  adAccountId: string;
  grantType: "PERMANENT" | "TEMPORARY";
  expiresAt: string | null;
  status: "ACTIVE" | "REVOKED" | "EXPIRED";
  reason: string | null;
  grantedBy: string;
  grantedAt: string;
  revokedAt: string | null;
}

export default function AdAccountDetailPage() {
  const { id = "" } = useParams();
  const me = useAuthStore((s) => s.user)!;
  const canManage = rolesAtLeast(me.role, "ORG_ADMIN");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showMock, setShowMock] = useState(false);

  const account = useQuery({
    queryKey: ["ad-account", id],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/ad-accounts/{id}", {
        params: { path: { id } },
      });
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as AdAccount;
    },
  });

  const grants = useQuery({
    queryKey: ["ad-account-grants", id],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/ad-accounts/{accountId}/grants", {
        params: { path: { accountId: id } },
      });
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as Grant[];
    },
    enabled: canManage,
  });

  const remove = useMutation({
    mutationFn: async () => {
      const { error, response } = await api.DELETE("/api/v1/ad-accounts/{id}", {
        params: { path: { id } },
      });
      if (error || !response.ok) throw error || new Error("删除失败");
    },
    onSuccess: () => {
      toast.success("已解除接入");
      navigate("/ad-accounts", { replace: true });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (account.isLoading) {
    return <div className="text-sm text-muted-foreground">加载中…</div>;
  }
  if (account.isError || !account.data) {
    return (
      <div className="space-y-3">
        <div className="text-destructive">无法加载账户。</div>
        <Link to="/ad-accounts" className="text-primary underline text-sm">返回列表</Link>
      </div>
    );
  }
  const a = account.data;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/ad-accounts" className="text-xs text-muted-foreground hover:underline">
          ← 返回广告账户列表
        </Link>
        <div className="flex items-end justify-between gap-3 flex-wrap mt-2">
          <div>
            <h1 className="text-2xl font-semibold">
              {a.name}
              {a.sandbox && (
                <span className="ml-2 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">
                  沙盒
                </span>
              )}
            </h1>
            <div className="font-mono text-xs text-muted-foreground mt-1">act_{a.metaAccountId}</div>
          </div>
          <div className="flex items-center gap-2">
            {canManage && a.sandbox && (
              <button
                onClick={() => setShowMock(true)}
                className="rounded-md bg-amber-100 text-amber-900 border border-amber-300 px-3 py-1.5 text-sm hover:bg-amber-200"
              >
                沙盒造数据
              </button>
            )}
            {canManage && (
              <button
                onClick={() => { if (confirm(`确定要解除接入 "${a.name}" 吗？`)) remove.mutate(); }}
                className="rounded-md border border-destructive text-destructive px-3 py-1.5 text-sm hover:bg-red-50"
              >
                解除接入
              </button>
            )}
          </div>
        </div>
      </div>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Stat label="币种" value={a.currency ?? "—"} />
        <Stat label="时区" value={a.timezoneName ?? "—"} />
        <Stat label="状态码" value={a.accountStatus?.toString() ?? "—"} />
        <Stat label="接入时间" value={new Date(a.createdAt).toLocaleDateString()} />
      </section>

      <CampaignsSection accountId={id} currency={a.currency ?? "USD"} />

      {canManage && <GrantsSection accountId={id} accountOrgId={a.organizationId} grants={grants.data ?? []} loading={grants.isLoading} qc={qc} />}

      {showMock && <MockSeedDialog accountId={id} onClose={() => setShowMock(false)} />}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium mt-1 break-all">{value}</div>
    </div>
  );
}

function GrantsSection({
  accountId, accountOrgId, grants, loading, qc,
}: {
  accountId: string;
  accountOrgId: string;
  grants: Grant[];
  loading: boolean;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const [type, setType] = useState<"PERMANENT" | "TEMPORARY">("PERMANENT");
  const [picked, setPicked] = useState<PickedUser | null>(null);
  const [expiresAt, setExpiresAt] = useState("");
  const [reason, setReason] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      if (!picked) throw new Error("请先选择用户");
      const payload =
        type === "PERMANENT"
          ? { userId: picked.id, grantType: "PERMANENT" as const }
          : {
              userId: picked.id,
              grantType: "TEMPORARY" as const,
              expiresAt: new Date(expiresAt).toISOString(),
              reason,
            };
      const { data, error, response } = await api.POST(
        "/api/v1/ad-accounts/{accountId}/grants",
        { params: { path: { accountId } }, body: payload }
      );
      if (error || !response.ok) throw error || new Error("授权失败");
      return data as unknown as Grant;
    },
    onSuccess: () => {
      setPicked(null);
      setExpiresAt("");
      setReason("");
      toast.success("授权成功");
      qc.invalidateQueries({ queryKey: ["ad-account-grants", accountId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const revoke = useMutation({
    mutationFn: async (grantId: string) => {
      const { error, response } = await api.DELETE(
        "/api/v1/ad-accounts/{accountId}/grants/{grantId}",
        { params: { path: { accountId, grantId } } }
      );
      if (error || !response.ok) throw error || new Error("撤销失败");
    },
    onSuccess: () => {
      toast.success("已撤销");
      qc.invalidateQueries({ queryKey: ["ad-account-grants", accountId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const active = useMemo(() => grants.filter((g) => g.status === "ACTIVE"), [grants]);
  const history = useMemo(() => grants.filter((g) => g.status !== "ACTIVE"), [grants]);

  return (
    <section className="space-y-4">
      <h2 className="font-medium text-lg">授权管理</h2>

      <div className="rounded-lg border border-border bg-background p-5">
        <h3 className="font-medium mb-3 text-sm">新增授权</h3>
        <div className="flex gap-2 mb-3">
          <Pill active={type === "PERMANENT"} onClick={() => { setType("PERMANENT"); setPicked(null); }}>永久</Pill>
          <Pill active={type === "TEMPORARY"} onClick={() => { setType("TEMPORARY"); setPicked(null); }}>临时（跨公司）</Pill>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
        >
          <div className="space-y-1.5 sm:col-span-2">
            <span className="text-sm font-medium">
              {type === "PERMANENT" ? "选择本公司用户" : "按邮箱查询跨公司用户"}
            </span>
            <UserPicker
              mode={type}
              organizationId={accountOrgId}
              value={picked}
              onChange={setPicked}
              excludeIds={grants.filter((g) => g.status === "ACTIVE" && g.grantType === type).map((g) => g.userId)}
            />
          </div>
          {type === "TEMPORARY" && (
            <>
              <label className="space-y-1.5">
                <span className="text-sm font-medium">过期时间</span>
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  required
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
                <span className="text-xs text-muted-foreground">最长 90 天</span>
              </label>
              <label className="space-y-1.5">
                <span className="text-sm font-medium">理由</span>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  maxLength={500}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
            </>
          )}
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={create.isPending || !picked}
              className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {create.isPending ? "提交中..." : "授权"}
            </button>
          </div>
        </form>
      </div>

      <GrantTable title="当前生效" rows={active} onRevoke={revoke.mutate} loading={loading} />
      {history.length > 0 && <GrantTable title="历史" rows={history} loading={false} />}
    </section>
  );
}

function GrantTable({
  title, rows, onRevoke, loading,
}: {
  title: string;
  rows: Grant[];
  onRevoke?: (id: string) => void;
  loading: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-background overflow-hidden">
      <div className="px-4 py-2 bg-muted text-sm font-medium">{title} ({rows.length})</div>
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs text-muted-foreground">
          <tr>
            <th className="text-left px-4 py-2 font-medium">用户 ID</th>
            <th className="text-left px-4 py-2 font-medium">类型</th>
            <th className="text-left px-4 py-2 font-medium">状态</th>
            <th className="text-left px-4 py-2 font-medium">过期</th>
            <th className="text-left px-4 py-2 font-medium">理由</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {loading && (<tr><td colSpan={6} className="px-4 py-4 text-center text-muted-foreground">加载中…</td></tr>)}
          {!loading && rows.length === 0 && (<tr><td colSpan={6} className="px-4 py-4 text-center text-muted-foreground">无</td></tr>)}
          {rows.map((g) => (
            <tr key={g.id} className="border-t border-border">
              <td className="px-4 py-2 font-mono text-xs">{g.userId.slice(0, 8)}…</td>
              <td className="px-4 py-2">
                {g.grantType === "PERMANENT"
                  ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">永久</span>
                  : <span className="text-xs px-2 py-0.5 rounded bg-violet-100 text-violet-800">临时</span>}
              </td>
              <td className="px-4 py-2">
                <StatusBadge status={g.status} />
              </td>
              <td className="px-4 py-2 text-xs text-muted-foreground">
                {g.expiresAt ? new Date(g.expiresAt).toLocaleString() : "—"}
              </td>
              <td className="px-4 py-2 text-xs">{g.reason ?? "—"}</td>
              <td className="px-4 py-2 text-right">
                {g.status === "ACTIVE" && onRevoke && (
                  <button
                    onClick={() => { if (confirm("撤销该授权？")) onRevoke(g.id); }}
                    className="text-xs text-destructive hover:underline"
                  >
                    撤销
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "ACTIVE"
      ? "bg-emerald-100 text-emerald-800"
      : status === "REVOKED"
      ? "bg-gray-200 text-gray-700"
      : "bg-rose-100 text-rose-800";
  return <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{status}</span>;
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
        active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"
      }`}
    >
      {children}
    </button>
  );
}
